import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiFetchJson } from '../../apiClient';
import './HomeDigest.css';

const unwrap = (data) =>
  Array.isArray(data) ? data : (data?.$values || []).filter((x) => x && !x.$ref);

function relativeTime(iso, t) {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const minutes = Math.round((Date.now() - then.getTime()) / 60000);
  if (minutes < 1) return t('home.justNow');
  if (minutes < 60) return t('home.minutesAgo', { count: minutes });

  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('home.hoursAgo', { count: hours });

  const days = Math.round(hours / 24);
  return t('home.daysAgo', { count: days });
}

function HomeDigest() {
  const { instance } = useMsal();
  const { t } = useTranslation();

  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);

  const loadDigest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchJson(instance, '/api/Home/digest');
      setDigest({
        since: data?.since ?? null,
        isFirstVisit: Boolean(data?.isFirstVisit),
        highlights: unwrap(data?.highlights),
        nudges: unwrap(data?.nudges)
      });
    } catch (e) {
      console.error('Failed to load home digest:', e);
      setError(t('home.loadError'));
    } finally {
      setLoading(false);
    }
  }, [instance, t]);

  useEffect(() => {
    loadDigest();
  }, [loadDigest]);

  const dismissNudge = useCallback(async (type) => {
    // Optimistic: the card disappears immediately, the server suppresses it for a day.
    setDigest((prev) =>
      prev ? { ...prev, nudges: prev.nudges.filter((n) => n.type !== type) } : prev
    );
    try {
      await apiFetchJson(instance, `/api/Notifications/dismiss/${type}`, { method: 'PUT' });
    } catch (e) {
      console.error('Failed to dismiss nudge:', e);
      loadDigest();
    }
  }, [instance, loadDigest]);

  const acknowledgeHighlights = useCallback(async () => {
    setAcknowledging(true);
    try {
      await apiFetchJson(instance, '/api/Home/seen', { method: 'POST' });
      setDigest((prev) => (prev ? { ...prev, highlights: [] } : prev));
    } catch (e) {
      console.error('Failed to mark digest as seen:', e);
    } finally {
      setAcknowledging(false);
    }
  }, [instance]);

  if (loading) {
    return (
      <section className="home-digest" aria-busy="true">
        <div className="digest-skeleton" />
        <div className="digest-skeleton" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="home-digest">
        <div className="digest-error" role="alert">
          <span>{error}</span>
          <button type="button" className="digest-retry" onClick={loadDigest}>
            {t('home.retry')}
          </button>
        </div>
      </section>
    );
  }

  if (!digest) return null;

  const { highlights, nudges, isFirstVisit } = digest;

  if (highlights.length === 0 && nudges.length === 0) {
    return (
      <section className="home-digest">
        <div className="digest-allclear">
          <span className="digest-allclear-icon" aria-hidden="true">✨</span>
          <div>
            <h2 className="digest-allclear-title">{t('home.allClearTitle')}</h2>
            <p className="digest-allclear-text">{t('home.allClearText')}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="home-digest" aria-label={t('home.digestLabel')}>
      {nudges.length > 0 && (
        <div className="digest-block">
          <div className="digest-head">
            <h2 className="digest-title">{t('home.needsYou')}</h2>
            <span className="digest-count">{nudges.length}</span>
          </div>
          <ul className="digest-list">
            {nudges.map((nudge) => (
              <li
                key={nudge.type}
                className={`digest-card digest-nudge${nudge.priority === 0 ? ' is-urgent' : ''}`}
              >
                <span className="digest-icon" aria-hidden="true">{nudge.icon}</span>
                <div className="digest-body">
                  <p className="digest-card-title">{nudge.title}</p>
                  <p className="digest-card-message">{nudge.message}</p>
                </div>
                <div className="digest-actions">
                  <Link to={nudge.route} className="digest-action">
                    {nudge.actionLabel}
                  </Link>
                  <button
                    type="button"
                    className="digest-dismiss"
                    onClick={() => dismissNudge(nudge.type)}
                    aria-label={t('home.dismiss', { title: nudge.title })}
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {highlights.length > 0 && (
        <div className="digest-block">
          <div className="digest-head">
            <h2 className="digest-title">
              {isFirstVisit ? t('home.recently') : t('home.sinceAway')}
            </h2>
            <button
              type="button"
              className="digest-ack"
              onClick={acknowledgeHighlights}
              disabled={acknowledging}
            >
              {acknowledging ? t('home.marking') : t('home.gotIt')}
            </button>
          </div>
          <ul className="digest-list">
            {highlights.map((h) => (
              <li key={`${h.type}-${h.occurredAt}`} className="digest-card digest-highlight">
                <span className="digest-icon" aria-hidden="true">{h.icon}</span>
                <div className="digest-body">
                  <p className="digest-card-title">{h.title}</p>
                  <p className="digest-card-message">{h.message}</p>
                </div>
                <div className="digest-actions">
                  <span className="digest-when">{relativeTime(h.occurredAt, t)}</span>
                  <Link to={h.route} className="digest-action digest-action-quiet">
                    {h.appName}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default HomeDigest;
